/**
 * Фильтр панели участников: его тексты и его разбор из хранилища.
 *
 * # Зачем отдельный модуль на две строки текста
 *
 * Потому что это ТЕКСТ ЧЕСТНОСТИ, а не подпись. Фильтр сужает таблицу до
 * меньшинства рынка, и на экране это выглядит ровно как полный состав: те же
 * колонки, тот же порядок, просто строк меньше. Единственное, что отличает одно
 * от другого, — эта фраза, и она обязана называть ЗНАМЕНАТЕЛЬ.
 *
 * Второе, что она обязана называть, — чего фильтр НЕ касается. Сводка по группам
 * считается по всем кошелькам снимка и от пометок не зависит вовсе; без оговорки
 * оператор прочитал бы её как сводку по отобранным.
 *
 * # Почему разбор хранилища живёт ЗДЕСЬ
 *
 * Тот же раздел «чистое/нечистое», что у lib/settings.ts и store/useSettings.ts:
 * правила разбора проверяются тестом без браузера, а ввод-вывод остаётся в
 * сторе. Правила здесь не косметические — см. decodeFilter: непроверенное
 * значение из хранилища уезжает В ЗАПРОС, и мусор в нём ломает панель насовсем.
 */
import type { ParticipantCohortKind, ParticipantsStats } from "../api/types";
import { COHORT_KINDS, TITLES } from "./cohorts";
import { plural } from "./format";

/**
 * Ключ хранилища. Версионирован по тому же правилу, что у настроек: сменится
 * смысл поля — сменится и ключ, а старое значение просто не прочитается.
 */
export const FILTER_STORAGE_KEY = "pm_scalper_participant_filter_v1";

/**
 * Потолки СЕРВЕРА, повторённые здесь намеренно.
 *
 * Не «на всякий случай»: сервер отвечает 400 на слишком длинный тег и на список
 * длиннее восьми, а этот список уезжает в КАЖДЫЙ запрос панели. Мусор,
 * попавший в хранилище (чужая версия, правка руками, битая запись), сделал бы
 * панель мёртвой навсегда — с пустой таблицей и ошибкой, причина которой лежит
 * в localStorage и на экране не видна ничем.
 */
const MAX_TAGS = 8;
const MAX_TAG_RUNES = 24;

/** Нажатое в фильтре: две пары списков — ручные пометки и подгруппы сводки. */
export interface ParticipantFilter {
  include: string[];
  exclude: string[];
  groupInclude: ParticipantCohortKind[];
  groupExclude: ParticipantCohortKind[];
}

/** Пустой фильтр — он же безопасное состояние при любой порче хранилища. */
export function emptyFilter(): ParticipantFilter {
  return { include: [], exclude: [], groupInclude: [], groupExclude: [] };
}

/**
 * Разбор сохранённого фильтра.
 *
 * # Почему любая порча даёт ПУСТОЙ фильтр, и молча
 *
 * Потому что пустой фильтр — единственное состояние, которое не врёт: таблица
 * показывает весь рынок, то есть ровно то, что и написано над ней. Потеря
 * фильтра при этом не потеря данных: он виден на экране всегда (счётчик на
 * кнопке, жёлтая строка со знаменателем, крестик снятия), и восстановить его —
 * два нажатия.
 *
 * # Что именно отбрасывается и почему это не паранойя
 *
 * Незнакомая подгруппа и слишком длинный тег дают на сервере 400 — а список
 * уезжает в каждый запрос панели. Прочитай мы такое значение как есть, и панель
 * оказалась бы мёртвой при живом сервере, причём навсегда: причина лежит в
 * хранилище, на экране не видна, а перезагрузка её не лечит.
 */
export function decodeFilter(raw: string | null): ParticipantFilter {
  if (!raw) return emptyFilter();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyFilter();
  }
  if (typeof parsed !== "object" || parsed === null) return emptyFilter();
  const o = parsed as Record<string, unknown>;
  return {
    include: tagList(o.include),
    exclude: tagList(o.exclude),
    groupInclude: kindList(o.groupInclude),
    groupExclude: kindList(o.groupExclude),
  };
}

/** Запись — та же форма, что читается. Порядок списков сохраняется как есть. */
export function encodeFilter(f: ParticipantFilter): string {
  return JSON.stringify({
    include: f.include,
    exclude: f.exclude,
    groupInclude: f.groupInclude,
    groupExclude: f.groupExclude,
  });
}

/** Список пометок из непроверенного значения: по правилам сервера. */
function tagList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== "string") continue;
    const tag = item.trim().toLowerCase();
    // Длина в СИМВОЛАХ, а не в единицах UTF-16: пометки русские, и потолок
    // сервера считается рунами.
    if (tag === "" || [...tag].length > MAX_TAG_RUNES) continue;
    if (!out.includes(tag)) out.push(tag);
    if (out.length === MAX_TAGS) break;
  }
  return out;
}

/** Список подгрупп: только те имена, которые знает и эта сторона, и сервер. */
function kindList(v: unknown): ParticipantCohortKind[] {
  if (!Array.isArray(v)) return [];
  const out: ParticipantCohortKind[] = [];
  for (const item of v) {
    const kind = item as ParticipantCohortKind;
    if (!COHORT_KINDS.includes(kind)) continue;
    if (!out.includes(kind)) out.push(kind);
  }
  return out;
}

/**
 * Строка под таблицей: по кому она отфильтрована и из скольких.
 *
 * Пусто, когда фильтра нет: молчание здесь верно — таблица описывает то же, что
 * описывала всегда.
 *
 * Читает ЭХО ответа (`tags_include`/`tags_exclude`), а не нажатые кнопки: между
 * нажатием и ответом на экране стоит прежняя таблица, и подписать её новым
 * фильтром значило бы соврать про состав строк — ровно та ошибка, ради которой
 * эхо и заведено.
 */
export function tagFilterNote(p: ParticipantsStats | undefined): string {
  if (!p?.known) return "";
  const include = p.tags_include ?? [];
  const exclude = p.tags_exclude ?? [];
  const groups = p.groups_include ?? [];
  const notGroups = p.groups_exclude ?? [];
  if (
    include.length === 0 &&
    exclude.length === 0 &&
    groups.length === 0 &&
    notGroups.length === 0
  ) {
    return "";
  }

  const parts: string[] = [];
  // Подгруппа — ПЕРВОЙ: её выбирают чаще («покажи плюсовых тейкеров»), и она
  // режет рынок сильнее. Имена берутся из общей таблицы, чтобы строка честности
  // и чип в строке называли одно и то же одинаково.
  if (groups.length > 0) {
    parts.push(
      groups.length === 1
        ? `показаны ${TITLES[groups[0]!]}`
        : `показаны любые из: ${groups.map((k) => TITLES[k]).join(", ")}`,
    );
  }
  if (notGroups.length > 0) {
    parts.push(`убраны ${notGroups.map((k) => TITLES[k]).join(", ")}`);
  }
  if (include.length > 0) {
    // «Любой из» — не придирка к слову: несколько тегов во включении читаются
    // как ИЛИ, и «показаны помеченные А и Б» прочиталось бы как требование обоих.
    parts.push(
      include.length === 1
        ? `показаны помеченные «${include[0]}»`
        : `показаны помеченные любым из: ${quoted(include)}`,
    );
  }
  if (exclude.length > 0) {
    // Исключение читается как И-НЕ: помеченный хоть одним из этих тегов убран.
    parts.push(`убраны помеченные ${quoted(exclude)}`);
  }

  const total = p.wallets_total;
  if (total !== undefined && total > 0) {
    parts.push(
      `${p.wallets} ${plural(p.wallets, "кошелёк", "кошелька", "кошельков")} ` +
        `из ${total} в окне`,
    );
  }
  // Сводка по группам живёт в той же карточке и считается по ВСЕМУ снимку.
  // Молчание об этом означало бы, что оператор прочитает её как сводку по
  // отобранным — то есть примет утверждение о рынке за утверждение о своей
  // группе, ровно наоборот.
  parts.push("сводку по группам фильтр не режет");
  return parts.join("; ");
}

/**
 * Причина, по которой пометок нет НИ У ОДНОЙ строки.
 *
 * Отдельно от фильтра, потому что это другой факт: «прочитали, помеченных нет» и
 * «не смогли прочитать» на экране выглядят одинаково — пустыми ячейками, — а
 * значат противоположное. Без этой строки оператор счёл бы, что его пометки
 * исчезли.
 */
export function tagFilterAlarm(p: ParticipantsStats | undefined): string {
  return p?.tags_reason ?? "";
}

function quoted(tags: readonly string[]): string {
  return tags.map((t) => `«${t}»`).join(", ");
}
